export const phoneRegExp =
  /^\+?([0-9]{1,4})?[ ]?(\(?\d{1,4}\)?)[ ]?(\d{1,4})[ ]?(\d{1,4})[ ]?(\d{1,9})$/;

//NAME
export const NAME_REGEX = /^[a-zA-Z\s'-]+$/;

// export const NAME_COMPANY_REGEX = /^[A-Za-z][A-Za-z\d\s/-]*$/;
export const NAME_COMPANY_REGEX = /^[A-Za-z\d#][A-Za-z\d\s/#-]*$/;
export const SWIFT_BIC_REGEX = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}(?:[A-Z0-9]{3})?$/i;
export const IBAN_REGEX = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/i;
export const US_ROUTING_NUMBER_REGEX = /^\d{9}$/;
// export const LETTERS_ONLY_NAME_COMPANY_REGEX = /^[A-Za-z\s\-_]{0,70}$/;
export const LETTERS_ONLY_NAME_COMPANY_REGEX = /^[A-Za-z\d\s\-_#]{0,70}$/;
export const LEGAL_ENTITY_NAME_REGEX = /^[\p{L}\p{N}\s.,&'()/#-]+$/u;

// POSTCODES
export const AUS_POSTCODE_REGEX = /^\d{4}$/; // AUS
export const USA_POSTCODE_REGEX = /^\d{5}(-\d{4})?$/; // USA
export const USA_SOCIAL_SECURITY_NUMBER_REGEX = /^\d{3}-?\d{2}-?\d{4}$/;

const POSTCODE_REGEX_BY_COUNTRY: Record<string, RegExp> = {
  AUS: AUS_POSTCODE_REGEX,
  AUT: /^\d{4}$/,
  BEL: /^\d{4}$/,
  BRA: /^\d{5}-?\d{3}$/,
  CAN: /^[A-Z]\d[A-Z][ -]?\d[A-Z]\d$/,
  CHE: /^\d{4}$/,
  CHN: /^\d{6}$/,
  DEU: /^\d{5}$/,
  DNK: /^\d{4}$/,
  ESP: /^\d{5}$/,
  FRA: /^\d{5}$/,
  GBR: /^(?:[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/,
  ITA: /^\d{5}$/,
  MEX: /^\d{5}$/,
  NLD: /^\d{4} ?[A-Z]{2}$/,
  NOR: /^\d{4}$/,
  NZL: AUS_POSTCODE_REGEX,
  SWE: /^\d{3} ?\d{2}$/,
  USA: USA_POSTCODE_REGEX,
};

const POSTCODE_EXAMPLE_BY_COUNTRY: Record<string, string> = {
  AUS: '2000',
  AUT: '1010',
  BEL: '1000',
  BRA: '01001-000',
  CAN: 'K1A 0B1',
  CHE: '3000',
  CHN: '100000',
  DEU: '10115',
  DNK: '1050',
  ESP: '28001',
  FRA: '75001',
  GBR: 'SW1A 1AA',
  ITA: '00118',
  MEX: '01000',
  NLD: '1012 JS',
  NOR: '0150',
  NZL: '6011',
  SWE: '111 20',
  USA: '10001',
};

// bronID defines country-specific passport formats only for Australia and New Zealand.
const PASSPORT_REGEX_BY_COUNTRY: Record<string, RegExp> = {
  AUS: /^[A-Z]{1,2}\d{7}$/,
  NZL: /^[A-Z]{2}\d{6}$/,
  UKR: /^[A-ZА-Я]{1,2}\d{6}$/,
};

const PASSPORT_EXAMPLE_BY_COUNTRY: Record<string, string> = {
  AUS: 'N1234567',
  AUT: 'C1234567',
  BEL: 'AB123456',
  BRA: 'AB123456',
  CAN: 'A123456AA',
  CHN: 'E12345678',
  CHE: 'X1234567',
  DEU: 'C01X00T47',
  DNK: '123456789',
  ESP: 'ABC123456',
  FRA: '12AB12345',
  GBR: '123456789',
  ITA: 'AB1234567',
  MEX: 'G12345678',
  NLD: 'N12345678',
  NOR: '123456789',
  NZL: 'AB123456',
  SWE: '12345678',
  UKR: 'AB123456',
  USA: 'A12345678',
};

const DRIVER_LICENSE_REGEX_BY_COUNTRY: Record<string, RegExp> = {
  AUS: /^[A-Z0-9]{6,10}$/,
  CHN: /^\d{12}$/,
  NZL: /^[A-Z]{2}\d{6}$/,
  USA: /^[A-Z0-9]{4,16}$/,
};

const NATIONAL_ID_REGEX_BY_COUNTRY: Record<string, RegExp> = {
  BRA: /^\d{11}$/,
  CAN: /^\d{3}-?\d{3}-?\d{3}$/,
  CHN: /^\d{17}[0-9X]$/,
  DNK: /^\d{6}-?\d{4}$/,
  ESP: /^[A-Z0-9]{9}$/,
  ITA: /^[A-Z0-9]{16}$/,
  MEX: /^[A-Z0-9]{18}$/,
  SWE: /^\d{6}-?\d{4}$/,
};

const CHINA_BANK_CARD_NUMBER_REGEX = /^\d{16,19}$/;
export const GIIN_REGEX = /^[A-NP-Z0-9]{6}\.[A-NP-Z0-9]{5}\.[A-NP-Z0-9]{2}\.[A-NP-Z0-9]{3}$/i;

const GENERIC_POSTCODE_REGEX = /^[A-Z0-9][A-Z0-9 -]{1,11}$/;
const GENERIC_DOCUMENT_NUMBER_REGEX = /^[A-Z0-9-]{4,20}$/;
const GENERIC_PASSPORT_NUMBER_REGEX = /^[A-Z0-9]{1,14}$/;

export const isValidPostcode = (country: string | undefined, value: string) => {
  const normalizedValue = value.trim().toUpperCase();
  const regex = country ? POSTCODE_REGEX_BY_COUNTRY[country] : undefined;

  return (regex ?? GENERIC_POSTCODE_REGEX).test(normalizedValue);
};

export const getPostcodeExample = (country: string | undefined) =>
  (country && POSTCODE_EXAMPLE_BY_COUNTRY[country]) || '12345';

export const isValidPassportNumber = (country: string | undefined, value: string) => {
  const normalizedValue = value.trim().toUpperCase();
  const regex = country ? PASSPORT_REGEX_BY_COUNTRY[country] : undefined;

  return (regex ?? GENERIC_PASSPORT_NUMBER_REGEX).test(normalizedValue);
};

export const getPassportNumberExample = (country: string | undefined) =>
  (country && PASSPORT_EXAMPLE_BY_COUNTRY[country]) || 'A12345678';

export const isValidDriverLicenseNumber = (country: string | undefined, value: string) => {
  const normalizedValue = value.trim().toUpperCase();
  const regex = country ? DRIVER_LICENSE_REGEX_BY_COUNTRY[country] : undefined;

  return (regex ?? GENERIC_DOCUMENT_NUMBER_REGEX).test(normalizedValue);
};

export const isValidNationalId = (country: string | undefined, value: string) => {
  const normalizedValue = value.trim().toUpperCase();
  const regex = country ? NATIONAL_ID_REGEX_BY_COUNTRY[country] : undefined;

  return !regex || regex.test(normalizedValue);
};

export const isValidChinaBankCardNumber = (value: string) =>
  CHINA_BANK_CARD_NUMBER_REGEX.test(value.trim());

// DRIVER LICENSE NUMBERS
export const AUS_LICENSE_REGEX = /^[A-Za-z0-9]{6,9}$/; // AUS
export const USA_LICENSE_REGEX = /^[A-Za-z0-9]{4,16}$/; // USA

// DRIVER LICENSE CARD NUMBERS
export const AUS_CARD_NUMBER_REGEX = /^[0-9]{6,10}$/; // AUS
export const USA_CARD_NUMBER_REGEX = /^[0-9]{4,12}$/; // USA

export const NZL_NZBN_NUMBER_REGEX = /^[0-9]{6,13}$/; // NZL

// PASSPORT NUMBER
export const AUS_PASSPORT_NUMBER = /^[A-Z]{1,2}\d{7}$/; // AUS
export const UKR_PASSPORT_NUMBER = /^[A-ZА-Я]{1,2}\d{6}$/; // UKR

// MEDICARE CARD NUMBER
export const AUS_MEDICARE_CARD_NUMBER = /^\d{4}[ /-]?\d{5}[ /-]?\d(?:[ /-]?\d)?$/; // AUS

// ACN & ABN
export const AUS_ACN = /^\d(?:\s?\d){8}$/; // AUS
export const AUS_ABN = /^\d(?:\s?\d){10}$/; // AUS

// Supports international address characters, including Spanish ordinal markers such as "3º".
export const REGISTER_OFFICE_REGEX =
  /^[\p{L}\p{N}\s.,'’#º°/()&-]+,\s*[\p{L}\p{N}\s.,'’#º°/()&-]+$/u;

export const SIX_DIGITS_WITH_SEPARATOR_REGEX = /^(\d[- ]?){6}$/gm;
export const NINE_DIGITS_WITH_SEPARATOR_REGEX = /^(\d[- ]?){8,9}$/gm;
export const ELEVEN_DIGITS_WITH_SEPARATOR_REGEX = /^(\d[- ]?){11}$/gm;
